import { useEffect, useRef, type RefObject } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useClient, useOrpc } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'

// Focus an input when its popover opens, WITHOUT scrolling the page. React's
// `autoFocus` (and Mantine's `trapFocus`) focus a `withinPortal` field before
// floating-ui has positioned the portal, so the browser scrolls it into view
// from the portal's initial top-of-document position — jerking the page to the
// top when a cell far down the list is opened. Deferring focus one frame and
// passing `preventScroll` avoids the jump. Pair with `trapFocus={false}`.
export function useAutofocusOnOpen<T extends HTMLElement>(opened: boolean): RefObject<T | null> {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (!opened) return
    const id = requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }))
    return () => cancelAnimationFrame(id)
  }, [opened])
  return ref
}

export type BoardData = Awaited<ReturnType<OrpcClient['boardGet']>>
export type Board = BoardData['board']
export type BoardItem = BoardData['items'][number]
export type ChecklistStep = NonNullable<BoardItem['checklist']>[number]
export type BoardColumn = Board['columns'][number]
export type BoardGroup = Board['groups'][number]
export type BoardNote = BoardItem['notes'][number]
export type BoardAttachment = NonNullable<BoardNote['attachments']>[number]
export type CellValue = BoardItem['values'][string]
export type CellRef = Extract<CellValue, { reference: string }>

// Done/total across an item's AI-generated follow-up steps (0/0 when there's no
// checklist), shared by the row badge and the drawer's completion count.
export function checklistCounts(item: BoardItem): { done: number; total: number } {
  const steps = item.checklist ?? []
  return { done: steps.filter((step) => step.done).length, total: steps.length }
}

function notifyError(title: string) {
  return (error: unknown) =>
    showNotification({
      color: 'red',
      title,
      message: error instanceof Error ? error.message : 'Unknown error',
    })
}

export function useBoardList() {
  const orpc = useOrpc()
  return useQuery(orpc.boardList.queryOptions({ input: {} }))
}

export function useBoard(boardId: string | undefined) {
  const orpc = useOrpc()
  return useQuery(orpc.boardGet.queryOptions({ input: { id: boardId ?? '' }, enabled: !!boardId }))
}

export function useAssignees() {
  const orpc = useOrpc()
  return useQuery(orpc.boardAssignees.queryOptions({ input: {}, staleTime: 300_000 }))
}

export function usePatientSearch(query: string) {
  const orpc = useOrpc()
  const trimmed = query.trim()
  return useQuery(
    orpc.boardPatientSearch.queryOptions({
      input: { query: trimmed },
      enabled: trimmed.length >= 2,
      placeholderData: keepPreviousData,
    }),
  )
}

export type CellProps = { item: BoardItem; column: BoardColumn }

// One-cell write helper for the cell components: send a single-column values
// patch (null clears the cell). Errors surface via the mutation's notification.
export function useUpdateItemValue(item: BoardItem) {
  const { updateItem } = useBoardMutations(item.boardId)
  return {
    // completionNote is only meaningful when this write completes a fax-linked
    // task (PD-1513); it's ignored server-side otherwise.
    update: (columnId: string, value: CellValue | null, completionNote?: string) =>
      updateItem
        .mutateAsync({
          itemId: item.id,
          values: { [columnId]: value },
          ...(completionNote ? { completionNote } : {}),
        })
        .catch(() => undefined),
    pending: updateItem.isPending,
  }
}

// All board mutations invalidate the one board query they affect (and the
// board list for creates), so every component re-renders from fresh data.
export function useBoardMutations(boardId?: string) {
  const orpc = useOrpc()
  const client = useClient()
  const queryClient = useQueryClient()

  const invalidateBoards = () => queryClient.invalidateQueries({ queryKey: orpc.boardList.key() })
  const invalidateBoard = () =>
    Promise.all([
      boardId
        ? queryClient.invalidateQueries({ queryKey: orpc.boardGet.key({ input: { id: boardId } }) })
        : undefined,
      // The patient chart lists the same items (PD-1870), and opens this drawer on them.
      queryClient.invalidateQueries({ queryKey: orpc.patientCareTasks.key() }),
    ])

  const createBoard = useMutation(
    orpc.boardCreate.mutationOptions({
      onSuccess: invalidateBoards,
      onError: notifyError('Failed to create board'),
    }),
  )
  const renameBoard = useMutation(
    orpc.boardRename.mutationOptions({
      onSuccess: () => Promise.all([invalidateBoards(), invalidateBoard()]),
      onError: notifyError('Failed to rename board'),
    }),
  )
  const deleteBoard = useMutation(
    orpc.boardDelete.mutationOptions({
      onSuccess: invalidateBoards,
      onError: notifyError('Failed to delete board'),
    }),
  )
  const addColumn = useMutation(
    orpc.boardAddColumn.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to add column'),
    }),
  )
  const renameColumn = useMutation(
    orpc.boardRenameColumn.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to rename column'),
    }),
  )
  const removeColumn = useMutation(
    orpc.boardRemoveColumn.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to remove column'),
    }),
  )
  // Optimistic so the dragged column header settles in its new spot immediately
  // instead of snapping back until the refetch lands.
  const reorderColumns = useMutation(
    orpc.boardReorderColumns.mutationOptions({
      onMutate: async ({ id, columnIds }: { id: string; columnIds: string[] }) => {
        // Use queryKey (the exact key), NOT key() — key() is a fuzzy match key for
        // invalidateQueries and won't resolve getQueryData/setQueryData against the
        // live board query, so the optimistic reorder would silently no-op and the
        // dragged column would snap back until the refetch lands.
        const key = orpc.boardGet.queryKey({ input: { id } })
        await queryClient.cancelQueries({ queryKey: key })
        const previous = queryClient.getQueryData<BoardData>(key)
        if (previous?.board) {
          const byId = new Map(previous.board.columns.map((c) => [c.id, c]))
          const columns = columnIds.map((cid) => byId.get(cid)).filter((c): c is BoardColumn => !!c)
          queryClient.setQueryData<BoardData>(key, {
            ...previous,
            board: { ...previous.board, columns },
          })
        }
        return { previous, key }
      },
      onError: (error, _vars, ctx) => {
        if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous)
        notifyError('Failed to reorder columns')(error)
      },
      onSettled: invalidateBoard,
    }),
  )
  const deleteItem = useMutation(
    orpc.boardItemDelete.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to delete task'),
    }),
  )
  const createItem = useMutation(
    orpc.boardItemCreate.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to add task'),
    }),
  )
  const updateItem = useMutation(
    orpc.boardItemUpdate.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to update task'),
    }),
  )
  const addNote = useMutation(
    orpc.boardNoteAdd.mutationOptions({
      onSuccess: invalidateBoard,
      onError: notifyError('Failed to add note'),
    }),
  )
  // Bulk actions fan out over the existing per-item endpoints — each stays
  // org-scoped and validated — and invalidate the board once, rather than
  // adding a dedicated batch endpoint.
  // Bulk actions settle every item even if some fail (allSettled), then always
  // invalidate the board (onSettled) so a partial success can't leave the cache
  // showing stale values or ghost rows. A partial failure still surfaces an error.
  const bulkUpdateItems = useMutation({
    mutationFn: async ({
      itemIds,
      values,
    }: {
      itemIds: string[]
      values: Record<string, CellValue | null>
    }) => {
      const results = await Promise.allSettled(
        itemIds.map((itemId) => client.boardItemUpdate({ itemId, values })),
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed) throw new Error(`${failed} of ${itemIds.length} tasks could not be updated`)
    },
    onSettled: invalidateBoard,
    onError: notifyError('Failed to update tasks'),
  })
  const bulkDeleteItems = useMutation({
    mutationFn: async (itemIds: string[]) => {
      const results = await Promise.allSettled(
        itemIds.map((itemId) => client.boardItemDelete({ itemId })),
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed) throw new Error(`${failed} of ${itemIds.length} tasks could not be deleted`)
    },
    onSettled: invalidateBoard,
    onError: notifyError('Failed to delete tasks'),
  })
  // Marking a task read is per-user server state (survives sign-out). Optimistically
  // flip its entry in the cached board's read map so the unread dot clears at once,
  // then reconcile with the server's authoritative map. Failures are silent — a
  // missed receipt just leaves the dot showing.
  const markRead = useMutation({
    // `seenAt` is used only for the optimistic cache write in onMutate; the server
    // computes the authoritative seen-time itself, so only `itemId` is sent (the
    // boardMarkRead contract takes just itemId — no contract mismatch).
    mutationFn: ({ itemId }: { itemId: string; seenAt: string }) =>
      client.boardMarkRead({ itemId }),
    // Use queryKey (the exact key), NOT key() — key() is a fuzzy match key for
    // invalidateQueries and won't resolve getQueryData/setQueryData against the
    // live board query, so the optimistic write would silently no-op.
    onMutate: ({ itemId, seenAt }: { itemId: string; seenAt: string }) => {
      if (!boardId) return
      const key = orpc.boardGet.queryKey({ input: { id: boardId } })
      const previous = queryClient.getQueryData<BoardData>(key)
      if (previous) {
        queryClient.setQueryData<BoardData>(key, {
          ...previous,
          readAt: { ...previous.readAt, [itemId]: seenAt },
        })
      }
      return { previous, key }
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous)
    },
    onSuccess: (result) => {
      if (!boardId) return
      const key = orpc.boardGet.queryKey({ input: { id: boardId } })
      const previous = queryClient.getQueryData<BoardData>(key)
      if (previous) queryClient.setQueryData<BoardData>(key, { ...previous, readAt: result.readAt })
    },
  })
  // Optimistic so a checkbox in the drawer flips immediately; reconciles with
  // the server on settle (same queryKey-not-key() caveat as reorderColumns/markRead).
  const toggleChecklistStep = useMutation({
    mutationFn: ({ itemId, stepId, done }: { itemId: string; stepId: string; done: boolean }) =>
      client.boardItemChecklistToggle({ itemId, stepId, done }),
    onMutate: async ({
      itemId,
      stepId,
      done,
    }: {
      itemId: string
      stepId: string
      done: boolean
    }) => {
      if (!boardId) return
      const key = orpc.boardGet.queryKey({ input: { id: boardId } })
      // Cancel any in-flight board refetch first, so it can't resolve mid-toggle
      // and clobber the optimistic checkbox before onSettled invalidation lands.
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<BoardData>(key)
      if (previous) {
        queryClient.setQueryData<BoardData>(key, {
          ...previous,
          items: previous.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  checklist: item.checklist?.map((step) =>
                    step.id === stepId ? { ...step, done } : step,
                  ),
                }
              : item,
          ),
        })
      }
      return { previous, key }
    },
    onError: (error, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous)
      notifyError('Failed to update checklist step')(error)
    },
    onSettled: invalidateBoard,
  })

  return {
    createBoard,
    renameBoard,
    deleteBoard,
    addColumn,
    renameColumn,
    reorderColumns,
    removeColumn,
    createItem,
    updateItem,
    deleteItem,
    addNote,
    bulkUpdateItems,
    bulkDeleteItems,
    markRead,
    toggleChecklistStep,
  }
}

export function useTrashList(enabled: boolean) {
  const orpc = useOrpc()
  return useQuery(orpc.boardTrashList.queryOptions({ input: {}, enabled }))
}

export function useTrashMutations() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.boardTrashList.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.boardList.key() }),
      // Restore/purge also change the Board admin table this tab renders above
      // Trash (boardAdminList) — so the board reappears without a page reload.
      queryClient.invalidateQueries({ queryKey: orpc.boardAdminList.key() }),
    ])
  const restoreBoard = useMutation(
    orpc.boardRestore.mutationOptions({
      onSuccess: invalidate,
      onError: notifyError('Failed to restore board'),
    }),
  )
  const purgeBoard = useMutation(
    orpc.boardPurge.mutationOptions({
      onSuccess: invalidate,
      onError: notifyError('Failed to permanently delete board'),
    }),
  )
  return { restoreBoard, purgeBoard }
}

export function useBoardAdminList(enabled: boolean) {
  const orpc = useOrpc()
  return useQuery(orpc.boardAdminList.queryOptions({ input: {}, enabled }))
}

export function useOwnerCandidates(boardId: string | undefined) {
  const orpc = useOrpc()
  return useQuery(
    orpc.boardOwnerCandidates.queryOptions({ input: { id: boardId ?? '' }, enabled: !!boardId }),
  )
}

export function useSetOwner() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.boardSetOwner.mutationOptions({
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.boardAdminList.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.boardList.key() }),
        ]),
      onError: notifyError('Failed to set board owner'),
    }),
  )
}
